import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  isTimeOffConflictError,
  saveTimeOff,
  TIME_OFF_WALL_CLOCK,
} from "./timeOffApi";

const NOTE_MAX_LENGTH = 255;
const DATE_FORMAT = "YYYY-MM-DD";

const isMidnight = (value) => dayjs(value).format("HH:mm:ss") === "00:00:00";

/**
 * Form state for a new block (on `initialDate`, default today), or for
 * editing `timeOff`.
 */
function initialForm(timeOff, stylistId, initialDate) {
  if (timeOff) {
    const start = dayjs(timeOff.start);
    const end = dayjs(timeOff.end);
    const allDay = isMidnight(timeOff.start) && isMidnight(timeOff.end);
    return {
      userId: timeOff.user_id ?? stylistId ?? "",
      allDay,
      startDate: start.format(DATE_FORMAT),
      // All-day blocks end at 00:00 of the day after the last day off.
      endDate: (allDay ? end.subtract(1, "day") : end).format(DATE_FORMAT),
      startTime: allDay ? "09:00" : start.format("HH:mm"),
      endTime: allDay ? "17:00" : end.format("HH:mm"),
      note: timeOff.note ?? "",
    };
  }
  const day = dayjs(initialDate ?? undefined);
  const date = (day.isValid() ? day : dayjs()).format(DATE_FORMAT);
  return {
    userId: stylistId ?? "",
    allDay: true,
    startDate: date,
    endDate: date,
    startTime: "09:00",
    endTime: "17:00",
    note: "",
  };
}

/** The [start, end) wall-clock strings the form describes. */
function spanFromForm(form) {
  if (form.allDay) {
    return {
      start: `${form.startDate} 00:00:00`,
      end: dayjs(form.endDate)
        .add(1, "day")
        .startOf("day")
        .format(TIME_OFF_WALL_CLOCK),
    };
  }
  return {
    start: `${form.startDate} ${form.startTime}:00`,
    end: `${form.endDate} ${form.endTime}:00`,
  };
}

const formatConflictTime = (value) => dayjs(value).format("MMM D, h:mm A");

/**
 * Record or edit a stylist's time off (JD-465). Shared by the scheduler and
 * the stylist form. `stylistId` preselects and locks the stylist; `timeOff`
 * switches to edit mode; `initialDate` is the day a new block starts on
 * (default today). When the API reports appointments inside the span,
 * the dialog lists them and offers "Record anyway".
 */
export default function TimeOffDialog({
  open,
  onClose,
  onSaved,
  stylists = [],
  stylistId,
  timeOff,
  initialDate,
}) {
  const [form, setForm] = useState(() =>
    initialForm(timeOff, stylistId, initialDate)
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null);

  const isEdit = Boolean(timeOff?.id);
  const stylistLocked = stylistId != null && stylistId !== "";

  useEffect(() => {
    if (!open) return;
    setForm(initialForm(timeOff, stylistId, initialDate));
    setError("");
    setConflicts(null);
    setSaving(false);
  }, [open, timeOff, stylistId, initialDate]);

  const update = (name) => (event) => {
    const value =
      name === "allDay" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const submit = useCallback(
    async (acknowledgeConflicts) => {
      if (!form.userId) {
        setError("Please choose a stylist.");
        return;
      }
      if (!form.startDate || !form.endDate) {
        setError("Please choose the start and end dates.");
        return;
      }
      if (!form.allDay && (!form.startTime || !form.endTime)) {
        setError("Please choose the start and end times.");
        return;
      }
      const { start, end } = spanFromForm(form);
      if (end <= start) {
        setError("The end must be after the start.");
        return;
      }

      setSaving(true);
      try {
        const result = await saveTimeOff(
          {
            user_id: Number(form.userId),
            start,
            end,
            note: form.note.trim() || null,
            ...(acknowledgeConflicts ? { acknowledge_conflicts: true } : {}),
          },
          timeOff?.id
        );
        toast.success("Time off saved");
        onSaved?.(result);
        onClose?.();
      } catch (err) {
        if (isTimeOffConflictError(err)) {
          setConflicts(err.response.data.conflicts || []);
        } else {
          toast.error(err?.response?.data?.message || "Could not save time off");
        }
      } finally {
        setSaving(false);
      }
    },
    [form, onClose, onSaved, timeOff]
  );

  const showingConflicts = Array.isArray(conflicts);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: "secondary.main",
          color: "common.white",
          borderBottom: "1px solid",
          borderColor: "primary.main",
          px: 3,
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="inherit">
            {isEdit ? "Edit time off" : "Record time off"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
          >
            The stylist cannot be booked during this time, at any location.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={saving}
          size="small"
          sx={{
            color: "primary.main",
            mt: 0.25,
            "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: "20px !important", pb: 2 }}>
        {showingConflicts ? (
          <Stack spacing={1.5}>
            <Alert severity="warning">
              This stylist has appointments during this time off
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Recording it anyway leaves these appointments in place. Move or
              cancel them separately if needed.
            </Typography>
            <List dense disablePadding>
              {conflicts.map((conflict) => (
                <ListItem
                  key={conflict.appointmentId}
                  disableGutters
                  divider
                >
                  <ListItemText
                    primary={conflict.clientName || "Client"}
                    secondary={`${formatConflictTime(
                      conflict.start
                    )} – ${formatConflictTime(conflict.end)}${
                      conflict.locationName ? ` · ${conflict.locationName}` : ""
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <FormControl
              fullWidth
              size="small"
              color="primary"
              disabled={saving || stylistLocked}
            >
              <InputLabel id="time-off-stylist-label">Stylist</InputLabel>
              <Select
                labelId="time-off-stylist-label"
                label="Stylist"
                value={form.userId}
                onChange={update("userId")}
              >
                {stylists.map((stylist) => (
                  <MenuItem key={stylist.user_id} value={stylist.user_id}>
                    {stylist.user_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={form.allDay}
                  onChange={update("allDay")}
                  color="primary"
                  disabled={saving}
                />
              }
              label="All day"
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Start date"
                type="date"
                size="small"
                fullWidth
                value={form.startDate}
                onChange={update("startDate")}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
              />
              {form.allDay ? null : (
                <TextField
                  label="Start time"
                  type="time"
                  size="small"
                  fullWidth
                  value={form.startTime}
                  onChange={update("startTime")}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
              )}
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="End date"
                type="date"
                size="small"
                fullWidth
                value={form.endDate}
                onChange={update("endDate")}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
              />
              {form.allDay ? null : (
                <TextField
                  label="End time"
                  type="time"
                  size="small"
                  fullWidth
                  value={form.endTime}
                  onChange={update("endTime")}
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
              )}
            </Stack>

            <TextField
              label="Note"
              placeholder="e.g. Vacation"
              size="small"
              fullWidth
              value={form.note}
              onChange={update("note")}
              disabled={saving}
              inputProps={{ maxLength: NOTE_MAX_LENGTH }}
            />

            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          gap: 1,
        }}
      >
        <Button
          type="button"
          onClick={showingConflicts ? () => setConflicts(null) : handleClose}
          disabled={saving}
          variant="outlined"
          color="secondary"
          sx={{ textTransform: "none", minWidth: 100 }}
        >
          {showingConflicts ? "Back" : "Cancel"}
        </Button>
        <Button
          type="button"
          onClick={() => submit(showingConflicts)}
          variant="contained"
          color={showingConflicts ? "warning" : "primary"}
          disabled={saving}
          sx={{
            minWidth: 140,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : showingConflicts ? (
            "Record anyway"
          ) : (
            "Save time off"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
